import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/App';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, Plus, Calendar, FileText, Trash2, ChevronRight, ChevronLeft, ChevronDown, ChevronUp, MoveUp, MoveDown, GripVertical } from 'lucide-react';
import { toast } from '@/components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Pricing data for display
const pricingData = {
  setup: {
    essentials: '$15,000 – $35,000',
    plus: '$40,000 – $85,000',
    pro: 'Custom Pricing'
  },
  hosting: {
    essentials: '$150 – $450/month',
    plus: '$500 – $1,500/month',
    pro: '$2,000 – $8,000+/month'
  },
  support: {
    essentials: '$1,000 – $2,500/month',
    plus: '$3,000 – $6,500/month',
    pro: '$8,000 – $18,000+/month',
    none: '$175 – $225/hour'
  }
};

const getPricing = (type, tier) => {
  if (!tier) return '';
  const tierKey = tier.toLowerCase();
  return pricingData[type]?.[tierKey] || '';
};

const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const { token, user } = useAuth();
  const [project, setProject] = useState(null);
  const [planRows, setPlanRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSubmissionDetails, setShowSubmissionDetails] = useState(false);
  const [wizardData, setWizardData] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const [pendingUpdates, setPendingUpdates] = useState({});
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [lastSelectedRow, setLastSelectedRow] = useState(null);
  const [dragState, setDragState] = useState({ isDragging: false, draggedRowId: null, dragOverRowId: null });
  const [internalNotes, setInternalNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  
  // Column resizing state
  const defaultColumnWidths = {
    drag: 24,
    checkbox: 32,
    rowNum: 48,
    milestone: 40,
    predecessor: 60,
    taskName: 250,
    startDate: 110,
    finishDate: 110,
    duration: 70,
    resetDate: 110,
    percent: 60,
    notes: 200,
    actions: 40
  };
  
  const [columnWidths, setColumnWidths] = useState(() => {
    // Load from localStorage if available
    const saved = localStorage.getItem(`projectPlanColumns_${projectId}`);
    return saved ? JSON.parse(saved) : defaultColumnWidths;
  });
  
  const [resizing, setResizing] = useState({ isResizing: false, column: null, startX: 0, startWidth: 0 });

  // Save column widths to localStorage when they change
  useEffect(() => {
    if (projectId) {
      localStorage.setItem(`projectPlanColumns_${projectId}`, JSON.stringify(columnWidths));
    }
  }, [columnWidths, projectId]);

  // Handle column resize
  const handleResizeStart = (e, columnKey) => {
    e.preventDefault();
    e.stopPropagation();
    setResizing({
      isResizing: true,
      column: columnKey,
      startX: e.clientX,
      startWidth: columnWidths[columnKey]
    });
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!resizing.isResizing) return;
      
      const diff = e.clientX - resizing.startX;
      const newWidth = Math.max(30, resizing.startWidth + diff); // Minimum 30px width
      
      setColumnWidths(prev => ({
        ...prev,
        [resizing.column]: newWidth
      }));
    };

    const handleMouseUp = () => {
      if (resizing.isResizing) {
        setResizing({ isResizing: false, column: null, startX: 0, startWidth: 0 });
      }
    };

    if (resizing.isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [resizing]);

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [projectRes, planRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`, { headers }),
        axios.get(`${API}/projects/${projectId}/plan`, { headers })
      ]);
      
      setProject(projectRes.data);
      setPlanRows(planRes.data);
      setInternalNotes(projectRes.data.internal_notes || '');
      
      if (projectRes.data.wizard_data) {
        try {
          setWizardData(JSON.parse(projectRes.data.wizard_data));
        } catch (e) {
          console.error('Failed to parse wizard data:', e);
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveInternalNotes = async () => {
    setSavingNotes(true);
    try {
      await axios.patch(
        `${API}/projects/${projectId}/internal-notes`,
        { internal_notes: internalNotes },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Internal notes saved');
    } catch (error) {
      console.error('Failed to save internal notes:', error);
      toast.error('Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const updateProjectStatus = async (newStatus) => {
    try {
      await axios.patch(`${API}/projects/${projectId}/status`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setProject({ ...project, status: newStatus });
      toast.success(`Project moved to ${newStatus}`);
    } catch (error) {
      console.error('Failed to update project status:', error);
      toast.error('Failed to update status');
    }
  };

  // Fields that require recalculation (dates/duration)
  const calculatedFields = ['start_date', 'finish_date', 'duration', 'milestone'];

  // Debounced cell update
  const debouncedUpdate = useCallback(
    (() => {
      const timeouts = {};
      return (rowId, field, value) => {
        const key = `${rowId}-${field}`;
        if (timeouts[key]) clearTimeout(timeouts[key]);
        timeouts[key] = setTimeout(async () => {
          try {
            const response = await axios.patch(
              `${API}/projects/${projectId}/plan/${rowId}`,
              { [field]: value },
              { headers: { Authorization: `Bearer ${token}` }}
            );
            // Only update local state with server response for calculated fields
            if (calculatedFields.includes(field)) {
              setPlanRows(rows => rows.map(row => 
                row.id === rowId ? { ...response.data } : row
              ));
            }
          } catch (error) {
            console.error('Failed to update cell:', error);
            toast.error('Failed to save changes');
          }
        }, 800);
      };
    })(),
    [projectId, token]
  );

  const handleCellChange = (rowId, field, value) => {
    // Update local state immediately for smooth typing
    setPlanRows(rows => rows.map(row => 
      row.id === rowId ? { ...row, [field]: value } : row
    ));
    // Debounce the API call
    debouncedUpdate(rowId, field, value);
  };

  // Toggle collapse state - separate from cell changes to avoid error toasts
  const toggleRowCollapse = async (rowId) => {
    const row = planRows.find(r => r.id === rowId);
    if (!row) return;
    
    const newCollapsedState = !row.is_collapsed;
    
    // Update local state immediately
    setPlanRows(rows => rows.map(r => 
      r.id === rowId ? { ...r, is_collapsed: newCollapsedState } : r
    ));
    
    // Save to backend silently (no error toast for UI-only state)
    try {
      await axios.patch(
        `${API}/projects/${projectId}/plan/${rowId}`,
        { is_collapsed: newCollapsedState },
        { headers: { Authorization: `Bearer ${token}` }}
      );
    } catch (error) {
      console.error('Failed to save collapse state:', error);
      // Don't show error toast - this is a UI-only preference
    }
  };

  const addNewRow = async () => {
    try {
      const response = await axios.post(
        `${API}/projects/${projectId}/plan`,
        { task_name: '', milestone: false },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setPlanRows([...planRows, response.data]);
      // Focus on the new task name cell
      setTimeout(() => {
        setEditingCell({ rowId: response.data.id, field: 'task_name' });
      }, 100);
    } catch (error) {
      console.error('Failed to add row:', error);
      toast.error('Failed to add row');
    }
  };

  // Insert row after a specific row or at end
  const insertRowAfter = async (afterRowId = null) => {
    try {
      const response = await axios.post(
        `${API}/projects/${projectId}/plan`,
        { task_name: '', milestone: false },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      if (afterRowId) {
        // Insert after the specified row
        const afterIndex = planRows.findIndex(r => r.id === afterRowId);
        if (afterIndex !== -1) {
          const newRows = [...planRows];
          newRows.splice(afterIndex + 1, 0, response.data);
          setPlanRows(newRows);
        } else {
          setPlanRows([...planRows, response.data]);
        }
      } else {
        setPlanRows([...planRows, response.data]);
      }
      
      // Focus on the new task name cell
      setTimeout(() => {
        setEditingCell({ rowId: response.data.id, field: 'task_name' });
      }, 100);
    } catch (error) {
      console.error('Failed to add row:', error);
      toast.error('Failed to add row');
    }
  };

  const deleteRow = async (rowId) => {
    if (!window.confirm('Delete this row?')) return;
    try {
      await axios.delete(
        `${API}/projects/${projectId}/plan/${rowId}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setPlanRows(rows => rows.filter(r => r.id !== rowId));
      setSelectedRows(prev => {
        const next = new Set(prev);
        next.delete(rowId);
        return next;
      });
      toast.success('Row deleted');
    } catch (error) {
      console.error('Failed to delete row:', error);
      toast.error('Failed to delete row');
    }
  };

  // Delete multiple selected rows
  const deleteSelectedRows = async () => {
    if (selectedRows.size === 0) return;
    if (!window.confirm(`Delete ${selectedRows.size} selected row(s)?`)) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await Promise.all(
        Array.from(selectedRows).map(rowId =>
          axios.delete(`${API}/projects/${projectId}/plan/${rowId}`, { headers })
        )
      );
      setPlanRows(rows => rows.filter(r => !selectedRows.has(r.id)));
      setSelectedRows(new Set());
      setLastSelectedRow(null);
      toast.success(`${selectedRows.size} row(s) deleted`);
    } catch (error) {
      console.error('Failed to delete rows:', error);
      toast.error('Failed to delete some rows');
    }
  };

  // Helper: Get all descendants of a row (children, grandchildren, etc.)
  const getRowWithDescendants = (rowIndex) => {
    const row = planRows[rowIndex];
    const rowLevel = row.indent_level || 0;
    const result = [rowIndex];
    
    for (let i = rowIndex + 1; i < planRows.length; i++) {
      const r = planRows[i];
      const rLevel = r.indent_level || 0;
      if (rLevel <= rowLevel) break;
      result.push(i);
    }
    return result;
  };

  // Move selected rows up
  const moveRowsUp = async () => {
    if (selectedRows.size === 0) return;
    
    // Get all selected row indices, sorted
    const selectedIndices = Array.from(selectedRows)
      .map(id => planRows.findIndex(r => r.id === id))
      .filter(idx => idx !== -1)
      .sort((a, b) => a - b);
    
    if (selectedIndices.length === 0) return;
    
    // Expand selection to include all descendants of parent rows
    const expandedIndices = new Set();
    selectedIndices.forEach(idx => {
      getRowWithDescendants(idx).forEach(i => expandedIndices.add(i));
    });
    const sortedExpanded = Array.from(expandedIndices).sort((a, b) => a - b);
    
    // Find the topmost selected index
    const topIndex = sortedExpanded[0];
    if (topIndex === 0) return; // Already at top
    
    // Find where to insert (above the row before the selection)
    // But we need to find the block above - if there's a parent with children above, move above the parent
    let targetIndex = topIndex - 1;
    const targetRow = planRows[targetIndex];
    const targetLevel = targetRow.indent_level || 0;
    
    // If we're moving into a different hierarchy level, find the parent of the target
    const movingRow = planRows[topIndex];
    const movingLevel = movingRow.indent_level || 0;
    
    // If target is at a deeper level than our row, we shouldn't move there
    // Find the row at same or lower level
    while (targetIndex > 0 && (planRows[targetIndex].indent_level || 0) > movingLevel) {
      targetIndex--;
    }
    
    // Now get the full block starting at targetIndex (including its descendants)
    const targetBlockIndices = getRowWithDescendants(targetIndex);
    const insertBeforeIndex = targetIndex;
    
    // Create new array with moved rows
    const newRows = [...planRows];
    const rowsToMove = sortedExpanded.map(i => newRows[i]);
    
    // Remove rows from their current positions (from end to start to preserve indices)
    [...sortedExpanded].reverse().forEach(i => {
      newRows.splice(i, 1);
    });
    
    // Calculate new insert position (adjusted for removed rows above)
    const removedAboveTarget = sortedExpanded.filter(i => i < insertBeforeIndex).length;
    const newInsertIndex = insertBeforeIndex - removedAboveTarget;
    
    // Insert rows at new position
    newRows.splice(newInsertIndex, 0, ...rowsToMove);
    
    // Update sort_order and persist
    const rowOrders = newRows.map((row, index) => ({
      id: row.id,
      sort_order: index + 1
    }));
    
    try {
      await axios.post(
        `${API}/projects/${projectId}/plan/reorder`,
        rowOrders,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setPlanRows(newRows);
      toast.success('Rows moved up');
    } catch (error) {
      console.error('Failed to reorder rows:', error);
      toast.error('Failed to move rows');
    }
  };

  // Move selected rows down
  const moveRowsDown = async () => {
    if (selectedRows.size === 0) return;
    
    // Get all selected row indices, sorted
    const selectedIndices = Array.from(selectedRows)
      .map(id => planRows.findIndex(r => r.id === id))
      .filter(idx => idx !== -1)
      .sort((a, b) => a - b);
    
    if (selectedIndices.length === 0) return;
    
    // Expand selection to include all descendants of parent rows
    const expandedIndices = new Set();
    selectedIndices.forEach(idx => {
      getRowWithDescendants(idx).forEach(i => expandedIndices.add(i));
    });
    const sortedExpanded = Array.from(expandedIndices).sort((a, b) => a - b);
    
    // Find the bottommost selected index
    const bottomIndex = sortedExpanded[sortedExpanded.length - 1];
    if (bottomIndex >= planRows.length - 1) return; // Already at bottom
    
    // Find the next row/block after our selection
    const nextIndex = bottomIndex + 1;
    const nextRow = planRows[nextIndex];
    const nextLevel = nextRow.indent_level || 0;
    
    // Get the full block of the next row (including its descendants)
    const nextBlockIndices = getRowWithDescendants(nextIndex);
    const insertAfterIndex = nextBlockIndices[nextBlockIndices.length - 1];
    
    // Create new array with moved rows
    const newRows = [...planRows];
    const rowsToMove = sortedExpanded.map(i => newRows[i]);
    
    // Remove rows from their current positions (from end to start to preserve indices)
    [...sortedExpanded].reverse().forEach(i => {
      newRows.splice(i, 1);
    });
    
    // Calculate new insert position (adjusted for removed rows)
    const removedBeforeInsert = sortedExpanded.filter(i => i <= insertAfterIndex).length;
    const newInsertIndex = insertAfterIndex - removedBeforeInsert + 1;
    
    // Insert rows at new position
    newRows.splice(newInsertIndex, 0, ...rowsToMove);
    
    // Update sort_order and persist
    const rowOrders = newRows.map((row, index) => ({
      id: row.id,
      sort_order: index + 1
    }));
    
    try {
      await axios.post(
        `${API}/projects/${projectId}/plan/reorder`,
        rowOrders,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setPlanRows(newRows);
      toast.success('Rows moved down');
    } catch (error) {
      console.error('Failed to reorder rows:', error);
      toast.error('Failed to move rows');
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, rowId) => {
    if (user?.role !== 'admin') return;
    
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', rowId);
    
    // Set drag state
    setDragState({ isDragging: true, draggedRowId: rowId, dragOverRowId: null });
    
    // Add dragging class to row for visual feedback
    e.target.closest('tr')?.classList.add('opacity-50');
  };

  const handleDragEnd = (e) => {
    e.target.closest('tr')?.classList.remove('opacity-50');
    setDragState({ isDragging: false, draggedRowId: null, dragOverRowId: null });
  };

  const handleDragOver = (e, rowId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (dragState.dragOverRowId !== rowId) {
      setDragState(prev => ({ ...prev, dragOverRowId: rowId }));
    }
  };

  const handleDragLeave = (e) => {
    // Only clear if we're leaving the row entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragState(prev => ({ ...prev, dragOverRowId: null }));
    }
  };

  const handleDrop = async (e, targetRowId) => {
    e.preventDefault();
    
    const draggedRowId = e.dataTransfer.getData('text/plain');
    if (!draggedRowId || draggedRowId === targetRowId) {
      setDragState({ isDragging: false, draggedRowId: null, dragOverRowId: null });
      return;
    }
    
    const draggedIndex = planRows.findIndex(r => r.id === draggedRowId);
    const targetIndex = planRows.findIndex(r => r.id === targetRowId);
    
    if (draggedIndex === -1 || targetIndex === -1) {
      setDragState({ isDragging: false, draggedRowId: null, dragOverRowId: null });
      return;
    }
    
    // Get the dragged row and all its descendants (for parent rows)
    const draggedBlockIndices = getRowWithDescendants(draggedIndex);
    
    // Check if we're trying to drop a parent into its own children
    if (draggedBlockIndices.includes(targetIndex)) {
      setDragState({ isDragging: false, draggedRowId: null, dragOverRowId: null });
      toast.error("Cannot move a parent into its own children");
      return;
    }
    
    // Create new array
    const newRows = [...planRows];
    const rowsToMove = draggedBlockIndices.map(i => newRows[i]);
    
    // Remove dragged rows from original positions (from end to preserve indices)
    [...draggedBlockIndices].reverse().forEach(i => {
      newRows.splice(i, 1);
    });
    
    // Calculate new target index (adjusted for removed rows above it)
    const removedAboveTarget = draggedBlockIndices.filter(i => i < targetIndex).length;
    let newTargetIndex = targetIndex - removedAboveTarget;
    
    // Insert before or after target based on drag direction
    if (draggedIndex < targetIndex) {
      // Moving down - insert after target
      newTargetIndex += 1;
    }
    
    // Insert rows at new position
    newRows.splice(newTargetIndex, 0, ...rowsToMove);
    
    // Update sort_order and persist
    const rowOrders = newRows.map((row, index) => ({
      id: row.id,
      sort_order: index + 1
    }));
    
    try {
      await axios.post(
        `${API}/projects/${projectId}/plan/reorder`,
        rowOrders,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setPlanRows(newRows);
    } catch (error) {
      console.error('Failed to reorder rows:', error);
      toast.error('Failed to reorder rows');
    }
    
    setDragState({ isDragging: false, draggedRowId: null, dragOverRowId: null });
  };

  // Handle row selection with Shift+Click support
  const handleRowSelect = (rowId, event, visibleRows) => {
    if (user?.role !== 'admin') return;
    
    if (event.shiftKey && lastSelectedRow) {
      // Shift+Click: select range
      const visibleIds = visibleRows.map(v => v.row.id);
      const startIdx = visibleIds.indexOf(lastSelectedRow);
      const endIdx = visibleIds.indexOf(rowId);
      if (startIdx !== -1 && endIdx !== -1) {
        const [from, to] = startIdx < endIdx ? [startIdx, endIdx] : [endIdx, startIdx];
        const rangeIds = visibleIds.slice(from, to + 1);
        setSelectedRows(prev => {
          const next = new Set(prev);
          rangeIds.forEach(id => next.add(id));
          return next;
        });
      }
    } else if (event.ctrlKey || event.metaKey) {
      // Ctrl/Cmd+Click: toggle single
      setSelectedRows(prev => {
        const next = new Set(prev);
        if (next.has(rowId)) {
          next.delete(rowId);
        } else {
          next.add(rowId);
        }
        return next;
      });
      setLastSelectedRow(rowId);
    } else {
      // Regular click: select only this row
      setSelectedRows(new Set([rowId]));
      setLastSelectedRow(rowId);
    }
  };

  // Keyboard event handler for grid
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (user?.role !== 'admin') return;
      
      // Ctrl/Cmd + Enter: Insert new row
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        // Insert after last selected row or at end
        const lastSelected = lastSelectedRow || (selectedRows.size > 0 ? Array.from(selectedRows).pop() : null);
        insertRowAfter(lastSelected);
        return;
      }
      
      // Delete/Backspace: Delete selected rows
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedRows.size > 0) {
        // Only trigger if not focused on an input/textarea
        const activeEl = document.activeElement;
        if (activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA' || activeEl.tagName === 'SELECT')) {
          return;
        }
        e.preventDefault();
        deleteSelectedRows();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [user, selectedRows, lastSelectedRow, planRows]);

  const getStatusClass = (status) => {
    const statusMap = {
      inquiry: 'status-initiation',
      follow_up: 'status-initiation',
      proposal_sent: 'status-planning',
      contract_signed: 'status-planning',
      initiation: 'status-initiation',
      planning: 'status-planning',
      execution: 'status-execution',
      monitoring: 'status-monitoring',
      closure: 'status-closure',
      active: 'status-active',
      completed: 'status-completed',
      on_hold: 'status-on-hold'
    };
    return statusMap[status] || 'status-inactive';
  };

  // Format date from YYYY-MM-DD to MM/DD/YYYY for display
  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const [year, month, day] = dateStr.split('-');
      return `${month}/${day}/${year}`;
    } catch (e) {
      return dateStr;
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      inquiry: '❓ Inquiry',
      follow_up: '📞 Follow-up',
      proposal_sent: '📄 Proposal Sent',
      contract_signed: '✍️ Contract Signed',
      initiation: '🎯 Initiation',
      planning: '📋 Planning',
      execution: '🚀 Execution',
      monitoring: '📊 Monitoring',
      closure: '✅ Closure',
      active: '🟢 Active',
      completed: '✨ Completed',
      on_hold: '⏸️ On Hold'
    };
    return labels[status] || status;
  };

  // Calculate overall progress from plan rows (only leaf nodes with task names)
  const calculateProgress = () => {
    if (planRows.length === 0) return 0;
    
    // Only count rows that have a task name (actual tasks, not empty rows)
    // and are not parent rows (to avoid double-counting)
    const leafRows = planRows.filter((row, index) => {
      // Skip empty rows
      if (!row.task_name || row.task_name.trim() === '') return false;
      
      // Check if this row is a parent (has children)
      const rowLevel = row.indent_level || 0;
      const isParent = planRows.some((r, i) => 
        i > index && 
        (r.indent_level || 0) > rowLevel &&
        !planRows.slice(index + 1, i).some(between => (between.indent_level || 0) <= rowLevel)
      );
      
      // Only include leaf nodes (non-parents)
      return !isParent;
    });
    
    if (leafRows.length === 0) return 0;
    
    const total = leafRows.reduce((sum, row) => sum + (row.percent_complete || 0), 0);
    return Math.round(total / leafRows.length);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-slate-gray">Loading project...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!project) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-slate-gray">Project not found</p>
        </div>
      </DashboardLayout>
    );
  }

  const overallProgress = calculateProgress();

  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          <Link to="/dashboard/projects" className="flex items-center text-fearless-blue hover:text-electric-indigo transition-colors w-fit">
            <ArrowLeft className="mr-2 h-4 w-4" />
            <span className="text-sm sm:text-base">Back to Projects</span>
          </Link>
          
          {/* Status Control */}
          <div className="flex-shrink-0">
            {user?.role === 'admin' ? (
              <Select value={project?.status} onValueChange={updateProjectStatus}>
                <SelectTrigger className="w-full sm:w-52">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inquiry">❓ Inquiry</SelectItem>
                  <SelectItem value="follow_up">📞 Follow-up</SelectItem>
                  <SelectItem value="proposal_sent">📄 Proposal Sent</SelectItem>
                  <SelectItem value="contract_signed">✍️ Contract Signed</SelectItem>
                  <SelectItem value="initiation">🎯 Initiation</SelectItem>
                  <SelectItem value="planning">📋 Planning</SelectItem>
                  <SelectItem value="execution">🚀 Execution</SelectItem>
                  <SelectItem value="monitoring">📊 Monitoring</SelectItem>
                  <SelectItem value="closure">✅ Closure</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <span className={`px-3 py-1.5 rounded-full text-sm font-semibold ${getStatusClass(project?.status)}`}>
                {getStatusLabel(project?.status)}
              </span>
            )}
          </div>
        </div>

        {/* Project Title */}
        <div>
          {project?.project_number && (
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-fearless-blue/10 text-fearless-blue text-sm font-mono font-semibold mb-2" data-testid="project-number-badge">
              {project.project_number}
            </div>
          )}
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-carbon-black break-words">{project?.title}</h1>
          <p className="text-slate-gray mt-2 text-sm sm:text-base break-words">{project?.description}</p>
        </div>

        {/* Project Info Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <Card className="shadow-lg rounded-xl border-0">
            <CardContent className="p-3 sm:p-6">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-fearless-blue" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-slate-gray">Start Date</p>
                  <p className="font-semibold text-carbon-black text-sm sm:text-base truncate">{formatDateDisplay(project?.start_date) || 'Not set'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg rounded-xl border-0">
            <CardContent className="p-3 sm:p-6">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-electric-indigo" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-slate-gray">End Date</p>
                  <p className="font-semibold text-carbon-black text-sm sm:text-base truncate">{formatDateDisplay(project?.end_date) || 'Not set'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg rounded-xl border-0">
            <CardContent className="p-3 sm:p-6">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-green-600 font-bold text-sm sm:text-base">$</span>
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-slate-gray">Budget</p>
                  <p className="font-semibold text-carbon-black text-sm sm:text-base truncate">${project?.budget?.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg rounded-xl border-0">
            <CardContent className="p-3 sm:p-6">
              <div className="min-w-0">
                <p className="text-xs text-slate-gray mb-1 sm:mb-2">Progress</p>
                <div className="flex items-center space-x-2">
                  <Progress value={overallProgress} className="flex-1 h-2" />
                  <span className="text-xs sm:text-sm font-semibold text-carbon-black flex-shrink-0">{overallProgress}%</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Submission Details Accordion */}
        {wizardData && (
          <Card className="shadow-lg rounded-xl border-0">
            <CardHeader 
              className="cursor-pointer hover:bg-gray-50 transition-colors rounded-t-xl"
              onClick={() => setShowSubmissionDetails(!showSubmissionDetails)}
            >
              <CardTitle className="flex items-center justify-between text-base sm:text-lg">
                <div className="flex items-center">
                  <FileText className="mr-2 h-4 w-4 sm:h-5 sm:w-5 text-fearless-blue" />
                  Submission Details
                </div>
                <span className="text-slate-gray text-xl">{showSubmissionDetails ? '−' : '+'}</span>
              </CardTitle>
            </CardHeader>
            {showSubmissionDetails && (
              <CardContent className="p-4 sm:p-6 pt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {/* Contact Info */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-carbon-black text-sm sm:text-base">Contact Information</h4>
                    <div className="space-y-2 text-xs sm:text-sm">
                      <p><span className="text-slate-gray">First Name:</span> <span className="font-medium">{wizardData.first_name || 'N/A'}</span></p>
                      <p><span className="text-slate-gray">Last Name:</span> <span className="font-medium">{wizardData.last_name || 'N/A'}</span></p>
                      <p><span className="text-slate-gray">Email:</span> <span className="font-medium">{wizardData.email || 'N/A'}</span></p>
                      <p><span className="text-slate-gray">Phone:</span> <span className="font-medium">{wizardData.phone_number || 'N/A'}</span></p>
                      <p><span className="text-slate-gray">Discovery Source:</span> <span className="font-medium">{wizardData.discovery_source || 'N/A'}</span></p>
                    </div>
                  </div>

                  {/* Business Info */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-carbon-black text-sm sm:text-base">Business Information</h4>
                    <div className="space-y-2 text-xs sm:text-sm">
                      <p><span className="text-slate-gray">Company:</span> <span className="font-medium">{wizardData.business_name || 'N/A'}</span></p>
                      <p><span className="text-slate-gray">Website:</span> <span className="font-medium">{wizardData.business_website || 'N/A'}</span></p>
                      <p><span className="text-slate-gray">Target Launch:</span> <span className="font-medium">{wizardData.target_launch_date || 'N/A'}</span></p>
                    </div>
                  </div>
                  
                  {/* Plans */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-carbon-black text-sm sm:text-base">Selected Plans</h4>
                    <div className="space-y-2 text-xs sm:text-sm">
                      <p><span className="text-slate-gray">Setup:</span> <span className="font-medium capitalize">{wizardData.setup_type || 'N/A'}</span> <span className="text-electric-indigo">{getPricing('setup', wizardData.setup_type)}</span></p>
                      <p><span className="text-slate-gray">Hosting:</span> <span className="font-medium capitalize">{wizardData.hosting_plan || 'N/A'}</span> <span className="text-electric-indigo">{getPricing('hosting', wizardData.hosting_plan)}</span></p>
                      <p><span className="text-slate-gray">Support:</span> <span className="font-medium capitalize">{wizardData.support_plan || 'N/A'}</span> <span className="text-electric-indigo">{getPricing('support', wizardData.support_plan)}</span></p>
                    </div>
                  </div>

                  {/* Google Integrations */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-carbon-black text-sm sm:text-base">Google Integrations</h4>
                    <div className="space-y-2 text-xs sm:text-sm">
                      <p><span className="text-slate-gray">Google Business Connected:</span> <span className="font-medium capitalize">{wizardData.google_business_connected || 'N/A'}</span></p>
                      <p><span className="text-slate-gray">Google Business URL:</span> <span className="font-medium">{wizardData.google_business_url || 'N/A'}</span></p>
                      <p><span className="text-slate-gray">Google Analytics Connected:</span> <span className="font-medium capitalize">{wizardData.google_analytics_connected || 'N/A'}</span></p>
                      <p><span className="text-slate-gray">Google Analytics ID:</span> <span className="font-medium">{wizardData.google_analytics_id || 'N/A'}</span></p>
                    </div>
                  </div>

                  {/* SEO */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-carbon-black text-sm sm:text-base">SEO Settings</h4>
                    <div className="space-y-2 text-xs sm:text-sm">
                      <p><span className="text-slate-gray">Local SEO Enabled:</span> <span className="font-medium capitalize">{wizardData.local_seo_enabled || 'N/A'}</span></p>
                      <p><span className="text-slate-gray">Target Keywords:</span> <span className="font-medium">{wizardData.target_keywords || 'N/A'}</span></p>
                    </div>
                  </div>

                  {/* Branding */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-carbon-black text-sm sm:text-base">Branding</h4>
                    <div className="space-y-2 text-xs sm:text-sm">
                      <p><span className="text-slate-gray">Has Logo:</span> <span className="font-medium capitalize">{wizardData.has_logo || 'N/A'}</span></p>
                      <p><span className="text-slate-gray">Has Website Copy:</span> <span className="font-medium capitalize">{wizardData.has_website_copy || 'N/A'}</span></p>
                      <p><span className="text-slate-gray">Has Brand Colors:</span> <span className="font-medium capitalize">{wizardData.has_brand_colors || 'N/A'}</span></p>
                      <p><span className="text-slate-gray">Brand Colors:</span> <span className="font-medium">{wizardData.brand_colors || 'N/A'}</span></p>
                      <p><span className="text-slate-gray">Reference Websites:</span> <span className="font-medium">{wizardData.reference_websites || 'N/A'}</span></p>
                    </div>
                  </div>

                  {/* Value Proposition */}
                  <div className="md:col-span-2 lg:col-span-3 space-y-2">
                    <h4 className="font-semibold text-carbon-black text-sm sm:text-base">Value Proposition</h4>
                    <p className="text-xs sm:text-sm text-slate-gray bg-gray-50 p-3 rounded-lg">{wizardData.value_proposition || 'N/A'}</p>
                  </div>

                  {/* XYZ Statement */}
                  <div className="md:col-span-2 lg:col-span-3 space-y-2">
                    <h4 className="font-semibold text-carbon-black text-sm sm:text-base">XYZ Statement</h4>
                    <p className="text-xs sm:text-sm text-slate-gray bg-gray-50 p-3 rounded-lg">{wizardData.xyz_statement || 'N/A'}</p>
                  </div>

                  {/* Configuration Notes */}
                  <div className="md:col-span-2 lg:col-span-3 space-y-2">
                    <h4 className="font-semibold text-carbon-black text-sm sm:text-base">Configuration Notes</h4>
                    <p className="text-xs sm:text-sm text-slate-gray bg-gray-50 p-3 rounded-lg">{wizardData.configuration_notes || 'N/A'}</p>
                  </div>
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {/* Internal Notes - Admin Only */}
        {user?.role === 'admin' && (
          <Card className="shadow-lg rounded-xl border-0">
            <CardHeader>
              <CardTitle className="flex items-center text-base sm:text-lg">
                <FileText className="mr-2 h-4 w-4 sm:h-5 sm:w-5 text-fearless-blue" />
                Internal Notes
                <span className="ml-2 text-xs font-normal text-slate-gray">(Admin Only)</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <textarea
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  placeholder="Add internal notes about this project..."
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-fearless-blue resize-none min-h-[120px]"
                />
                <div className="flex justify-end">
                  <Button 
                    onClick={saveInternalNotes} 
                    disabled={savingNotes}
                    className="gradient-fearless text-white"
                  >
                    {savingNotes ? 'Saving...' : 'Save Notes'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Project Plan Grid */}
        <Card className="shadow-lg rounded-xl border-0">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg sm:text-xl">Project Plan</CardTitle>
            {user?.role === 'admin' && (
              <Button onClick={addNewRow} className="gradient-fearless text-white" data-testid="add-row-btn">
                <Plus className="mr-2 h-4 w-4" />
                Add Row
              </Button>
            )}
          </CardHeader>
          <CardContent className="p-0">
            {/* Selection info bar */}
            {user?.role === 'admin' && selectedRows.size > 0 && (
              <div className="bg-blue-50 border-b border-blue-200 px-4 py-2 flex items-center justify-between">
                <span className="text-sm text-blue-700">
                  {selectedRows.size} row(s) selected
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={moveRowsUp}
                    className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-100 rounded transition-colors flex items-center"
                    title="Move Up"
                  >
                    <MoveUp className="h-4 w-4 mr-1" />
                    Move Up
                  </button>
                  <button
                    onClick={moveRowsDown}
                    className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-100 rounded transition-colors flex items-center"
                    title="Move Down"
                  >
                    <MoveDown className="h-4 w-4 mr-1" />
                    Move Down
                  </button>
                  <button
                    onClick={deleteSelectedRows}
                    className="px-3 py-1 text-sm text-red-600 hover:bg-red-100 rounded transition-colors flex items-center"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete Selected
                  </button>
                </div>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm" style={{ minWidth: 'max-content' }}>
                <thead className="bg-gray-50 border-b">
                  <tr>
                    {user?.role === 'admin' && (
                      <th style={{ width: columnWidths.drag }} className="px-1 py-3 relative border-r border-gray-300">
                        <div 
                          className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-fearless-blue/30 z-10 -mr-1"
                          onMouseDown={(e) => handleResizeStart(e, 'drag')}
                        />
                      </th>
                    )}
                    {user?.role === 'admin' && (
                      <th style={{ width: columnWidths.checkbox }} className="px-2 py-3 relative border-r border-gray-300">
                        <div 
                          className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-fearless-blue/30 z-10 -mr-1"
                          onMouseDown={(e) => handleResizeStart(e, 'checkbox')}
                        />
                      </th>
                    )}
                    <th style={{ width: columnWidths.rowNum }} className="px-2 py-3 text-left font-semibold text-slate-gray relative border-r border-gray-300">
                      #
                      <div 
                        className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-fearless-blue/30 z-10 -mr-1"
                        onMouseDown={(e) => handleResizeStart(e, 'rowNum')}
                      />
                    </th>
                    <th style={{ width: columnWidths.milestone }} className="px-2 py-3 text-left font-semibold text-slate-gray relative border-r border-gray-300">
                      MS
                      <div 
                        className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-fearless-blue/30 z-10 -mr-1"
                        onMouseDown={(e) => handleResizeStart(e, 'milestone')}
                      />
                    </th>
                    <th style={{ width: columnWidths.predecessor }} className="px-2 py-3 text-left font-semibold text-slate-gray relative border-r border-gray-300">
                      Pred
                      <div 
                        className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-fearless-blue/30 z-10 -mr-1"
                        onMouseDown={(e) => handleResizeStart(e, 'predecessor')}
                      />
                    </th>
                    <th style={{ width: columnWidths.taskName }} className="px-3 py-3 text-left font-semibold text-slate-gray relative border-r border-gray-300">
                      Task Name
                      <div 
                        className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-fearless-blue/30 z-10 -mr-1"
                        onMouseDown={(e) => handleResizeStart(e, 'taskName')}
                      />
                    </th>
                    <th style={{ width: columnWidths.startDate }} className="px-2 py-3 text-left font-semibold text-slate-gray relative border-r border-gray-300">
                      Start Date
                      <div 
                        className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-fearless-blue/30 z-10 -mr-1"
                        onMouseDown={(e) => handleResizeStart(e, 'startDate')}
                      />
                    </th>
                    <th style={{ width: columnWidths.finishDate }} className="px-2 py-3 text-left font-semibold text-slate-gray relative border-r border-gray-300">
                      Finish Date
                      <div 
                        className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-fearless-blue/30 z-10 -mr-1"
                        onMouseDown={(e) => handleResizeStart(e, 'finishDate')}
                      />
                    </th>
                    <th style={{ width: columnWidths.duration }} className="px-2 py-3 text-left font-semibold text-slate-gray relative border-r border-gray-300">
                      Days
                      <div 
                        className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-fearless-blue/30 z-10 -mr-1"
                        onMouseDown={(e) => handleResizeStart(e, 'duration')}
                      />
                    </th>
                    <th style={{ width: columnWidths.resetDate }} className="px-2 py-3 text-left font-semibold text-slate-gray relative border-r border-gray-300">
                      Reset Date
                      <div 
                        className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-fearless-blue/30 z-10 -mr-1"
                        onMouseDown={(e) => handleResizeStart(e, 'resetDate')}
                      />
                    </th>
                    <th style={{ width: columnWidths.percent }} className="px-2 py-3 text-left font-semibold text-slate-gray relative border-r border-gray-300">
                      %
                      <div 
                        className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-fearless-blue/30 z-10 -mr-1"
                        onMouseDown={(e) => handleResizeStart(e, 'percent')}
                      />
                    </th>
                    <th style={{ width: columnWidths.notes }} className="px-2 py-3 text-left font-semibold text-slate-gray relative border-r border-gray-300">
                      Notes
                      <div 
                        className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-fearless-blue/30 z-10 -mr-1"
                        onMouseDown={(e) => handleResizeStart(e, 'notes')}
                      />
                    </th>
                    {user?.role === 'admin' && (
                      <th style={{ width: columnWidths.actions }} className="px-2 py-3"></th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {planRows.length === 0 ? (
                    <tr>
                      <td colSpan={user?.role === 'admin' ? 14 : 10} className="px-3 py-12 text-center text-slate-gray">
                        No plan rows yet. {user?.role === 'admin' ? 'Click "Add Row" to start planning. (Ctrl/Cmd+Enter to insert row)' : ''}
                      </td>
                    </tr>
                  ) : (
                    (() => {
                      // Helper function to get all descendants of a row
                      const getDescendants = (rowIndex, rowLevel) => {
                        const descendants = [];
                        for (let i = rowIndex + 1; i < planRows.length; i++) {
                          const r = planRows[i];
                          const rLevel = r.indent_level || 0;
                          if (rLevel <= rowLevel) break; // No longer a descendant
                          descendants.push(r);
                        }
                        return descendants;
                      };

                      // Helper function to calculate rollup values for a parent row
                      const calculateRollups = (rowIndex, rowLevel) => {
                        const descendants = getDescendants(rowIndex, rowLevel);
                        if (descendants.length === 0) return null;

                        // Filter to only leaf nodes (rows without children) for % calculation
                        const leafNodes = descendants.filter((d, i) => {
                          const dIndex = planRows.indexOf(d);
                          const dLevel = d.indent_level || 0;
                          // Check if this descendant has children
                          const hasChildren = planRows.some((r, ri) => 
                            ri > dIndex && (r.indent_level || 0) > dLevel &&
                            !planRows.slice(dIndex + 1, ri).some(between => (between.indent_level || 0) <= dLevel)
                          );
                          return !hasChildren;
                        });

                        // Calculate earliest start date
                        const startDates = descendants
                          .filter(d => d.start_date)
                          .map(d => new Date(d.start_date));
                        const earliestStart = startDates.length > 0 
                          ? new Date(Math.min(...startDates)).toISOString().split('T')[0]
                          : null;

                        // Calculate latest finish date
                        const finishDates = descendants
                          .filter(d => d.finish_date)
                          .map(d => new Date(d.finish_date));
                        const latestFinish = finishDates.length > 0
                          ? new Date(Math.max(...finishDates)).toISOString().split('T')[0]
                          : null;

                        // Calculate duration from rollup dates
                        let rollupDuration = 0;
                        if (earliestStart && latestFinish) {
                          const start = new Date(earliestStart);
                          const finish = new Date(latestFinish);
                          rollupDuration = Math.round((finish - start) / (1000 * 60 * 60 * 24));
                        }

                        // Calculate average % complete from leaf nodes only
                        const percentValues = leafNodes
                          .map(d => d.percent_complete || 0);
                        const avgPercent = percentValues.length > 0
                          ? Math.round(percentValues.reduce((a, b) => a + b, 0) / percentValues.length)
                          : 0;

                        return {
                          start_date: earliestStart,
                          finish_date: latestFinish,
                          duration: rollupDuration,
                          percent_complete: avgPercent
                        };
                      };

                      // Compute visible rows based on collapse state
                      const visibleRows = [];
                      let skipUntilLevel = null;
                      
                      planRows.forEach((row, originalIndex) => {
                        const rowLevel = row.indent_level || 0;
                        
                        // Check if we should skip this row due to a collapsed parent
                        if (skipUntilLevel !== null) {
                          if (rowLevel > skipUntilLevel) {
                            return; // Skip this row
                          } else {
                            skipUntilLevel = null; // Reset, we're out of the collapsed section
                          }
                        }
                        
                        // Check if this row is a parent (has children indented below it)
                        const isParent = planRows.some((r, i) => 
                          i > originalIndex && 
                          (r.indent_level || 0) > rowLevel &&
                          !planRows.slice(originalIndex + 1, i).some(between => (between.indent_level || 0) <= rowLevel)
                        );

                        // Calculate rollup values for parent rows
                        const rollups = isParent ? calculateRollups(originalIndex, rowLevel) : null;
                        
                        visibleRows.push({ row, originalIndex, isParent, rollups });
                        
                        // If this row is collapsed and is a parent, skip its children
                        if (row.is_collapsed && isParent) {
                          skipUntilLevel = rowLevel;
                        }
                      });
                      
                      return visibleRows.map(({ row, originalIndex, isParent, rollups }, visibleIndex) => {
                        // Use rollup values for parent rows, otherwise use row values
                        const displayStartDate = isParent && rollups?.start_date ? rollups.start_date : row.start_date;
                        const displayFinishDate = isParent && rollups?.finish_date ? rollups.finish_date : row.finish_date;
                        const displayDuration = isParent && rollups ? rollups.duration : row.duration;
                        const displayPercent = isParent && rollups ? rollups.percent_complete : (row.percent_complete || 0);
                        const isSelected = selectedRows.has(row.id);
                        const isDragOver = dragState.dragOverRowId === row.id && dragState.draggedRowId !== row.id;

                        return (
                        <tr 
                          key={row.id} 
                          className={`border-b hover:bg-gray-50 transition-colors align-top ${isSelected ? 'bg-blue-50' : ''} ${isDragOver ? 'border-t-2 border-t-fearless-blue' : ''}`} 
                          data-testid={`plan-row-${row.id}`}
                          onDragOver={(e) => handleDragOver(e, row.id)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, row.id)}
                          onClick={(e) => {
                            // Only handle row selection if clicking on the row itself (not inputs)
                            if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA' && e.target.tagName !== 'SELECT' && e.target.tagName !== 'BUTTON') {
                              handleRowSelect(row.id, e, visibleRows);
                            }
                          }}
                        >
                          {/* Drag Handle */}
                          {user?.role === 'admin' && (
                            <td style={{ width: columnWidths.drag }} className="px-1 py-2">
                              <div
                                draggable
                                onDragStart={(e) => handleDragStart(e, row.id)}
                                onDragEnd={handleDragEnd}
                                className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-200 rounded transition-colors"
                                title="Drag to reorder"
                              >
                                <GripVertical className="h-4 w-4 text-gray-400" />
                              </div>
                            </td>
                          )}
                          
                          {/* Selection Checkbox */}
                          {user?.role === 'admin' && (
                            <td style={{ width: columnWidths.checkbox }} className="px-2 py-2">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleRowSelect(row.id, e, visibleRows);
                                }}
                                className="h-4 w-4 rounded border-gray-300 text-fearless-blue focus:ring-fearless-blue cursor-pointer"
                              />
                            </td>
                          )}
                          
                          {/* Row # (based on visible index) */}
                          <td style={{ width: columnWidths.rowNum }} className="px-2 py-2 text-slate-gray font-medium">
                            {visibleIndex + 1}
                          </td>
                          
                          {/* Milestone Checkbox */}
                          <td style={{ width: columnWidths.milestone }} className="px-2 py-2">
                            <input
                              type="checkbox"
                              checked={row.milestone || false}
                              onChange={(e) => handleCellChange(row.id, 'milestone', e.target.checked)}
                              disabled={user?.role !== 'admin' || isParent}
                              className="h-4 w-4 rounded border-gray-300 text-fearless-blue focus:ring-fearless-blue"
                            />
                          </td>
                          
                          {/* Predecessor */}
                          <td style={{ width: columnWidths.predecessor }} className="px-2 py-2">
                            {user?.role === 'admin' ? (
                              <input
                                type="number"
                                min="1"
                                max={planRows.length}
                                value={row.predecessor_id ? planRows.findIndex(r => r.id === row.predecessor_id) + 1 : ''}
                                onChange={(e) => {
                                  const rowNum = parseInt(e.target.value);
                                  if (!e.target.value || e.target.value === '') {
                                    handleCellChange(row.id, 'predecessor_id', null);
                                  } else if (rowNum >= 1 && rowNum <= planRows.length) {
                                    const targetRow = planRows[rowNum - 1];
                                    if (targetRow && targetRow.id !== row.id) {
                                      handleCellChange(row.id, 'predecessor_id', targetRow.id);
                                    }
                                  }
                                }}
                                onFocus={(e) => e.target.select()}
                                placeholder="-"
                                className="w-full px-1 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-fearless-blue [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              />
                            ) : (
                              <span className="text-slate-gray">
                                {row.predecessor_id ? planRows.findIndex(r => r.id === row.predecessor_id) + 1 : '-'}
                              </span>
                            )}
                          </td>
                          
                          {/* Task Name with indentation, collapse/expand, and word wrap */}
                          <td style={{ width: columnWidths.taskName }} className="px-3 py-2">
                            <div className="flex items-start" style={{ paddingLeft: `${(row.indent_level || 0) * 20}px` }}>
                              {/* Collapse/Expand toggle for parent rows */}
                              {isParent ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleRowCollapse(row.id);
                                  }}
                                  className="p-0.5 mr-1 text-gray-500 hover:text-gray-700 flex-shrink-0"
                                  title={row.is_collapsed ? 'Expand' : 'Collapse'}
                                >
                                  <ChevronDown className={`h-4 w-4 transition-transform ${row.is_collapsed ? '-rotate-90' : ''}`} />
                                </button>
                              ) : (
                                <span className="w-5 flex-shrink-0"></span>
                              )}
                              
                              {user?.role === 'admin' && (
                                <div className="flex mr-1 flex-shrink-0">
                                  <button
                                    onClick={() => handleCellChange(row.id, 'indent_level', Math.max(0, (row.indent_level || 0) - 1))}
                                    disabled={(row.indent_level || 0) === 0}
                                    className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                                    title="Outdent"
                                  >
                                    <ChevronLeft className="h-3 w-3" />
                                  </button>
                                  <button
                                    onClick={() => handleCellChange(row.id, 'indent_level', (row.indent_level || 0) + 1)}
                                    disabled={(row.indent_level || 0) >= 5}
                                    className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                                    title="Indent"
                                  >
                                    <ChevronRight className="h-3 w-3" />
                                  </button>
                                </div>
                              )}
                              {user?.role === 'admin' ? (
                                <textarea
                                  value={row.task_name || ''}
                                  onChange={(e) => handleCellChange(row.id, 'task_name', e.target.value)}
                                  placeholder="Enter task name..."
                                  className={`flex-1 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-fearless-blue resize-none whitespace-pre-wrap break-words ${(row.milestone || isParent) ? 'font-bold' : ''}`}
                                  style={{ minHeight: '32px', height: 'auto', overflow: 'hidden' }}
                                  ref={(el) => {
                                    if (el) {
                                      el.style.height = 'auto';
                                      el.style.height = el.scrollHeight + 'px';
                                    }
                                  }}
                                  onInput={(e) => {
                                    e.target.style.height = 'auto';
                                    e.target.style.height = e.target.scrollHeight + 'px';
                                  }}
                                  autoFocus={editingCell?.rowId === row.id && editingCell?.field === 'task_name'}
                                />
                              ) : (
                                <span className={`whitespace-pre-wrap break-words ${(row.milestone || isParent) ? 'font-bold' : ''}`}>{row.task_name || '-'}</span>
                              )}
                            </div>
                          </td>
                        
                          {/* Start Date */}
                          <td style={{ width: columnWidths.startDate }} className="px-2 py-2">
                            {isParent ? (
                              <span className="text-sm text-slate-gray bg-gray-100 px-2 py-1 rounded block">
                                {formatDateDisplay(displayStartDate)}
                              </span>
                            ) : user?.role === 'admin' ? (
                              <input
                                type="date"
                                value={row.start_date || ''}
                                onChange={(e) => handleCellChange(row.id, 'start_date', e.target.value)}
                                className="w-full px-1 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-fearless-blue"
                              />
                            ) : (
                              <span>{formatDateDisplay(row.start_date)}</span>
                            )}
                          </td>
                        
                          {/* Finish Date */}
                          <td style={{ width: columnWidths.finishDate }} className="px-2 py-2">
                            {isParent ? (
                              <span className="text-sm text-slate-gray bg-gray-100 px-2 py-1 rounded block">
                                {formatDateDisplay(displayFinishDate)}
                              </span>
                            ) : user?.role === 'admin' ? (
                              <input
                                type="date"
                                value={row.finish_date || ''}
                                onChange={(e) => handleCellChange(row.id, 'finish_date', e.target.value)}
                                className="w-full px-1 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-fearless-blue"
                              />
                            ) : (
                              <span>{formatDateDisplay(row.finish_date)}</span>
                            )}
                          </td>
                        
                          {/* Duration */}
                          <td style={{ width: columnWidths.duration }} className="px-2 py-2">
                            {isParent ? (
                              <span className="text-sm text-slate-gray bg-gray-100 px-2 py-1 rounded block text-center">
                                {displayDuration}
                              </span>
                            ) : user?.role === 'admin' ? (
                              <input
                                type="number"
                                min="0"
                                value={row.duration || ''}
                                onChange={(e) => handleCellChange(row.id, 'duration', parseInt(e.target.value) || 0)}
                                onFocus={(e) => e.target.select()}
                                className="w-full px-1 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-fearless-blue [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                disabled={row.milestone}
                              />
                            ) : (
                              <span>{row.duration || 0}</span>
                            )}
                          </td>
                        
                          {/* Reset Date */}
                          <td style={{ width: columnWidths.resetDate }} className="px-2 py-2">
                            {user?.role === 'admin' ? (
                              <input
                                type="date"
                                value={row.reset_date || ''}
                                onChange={(e) => handleCellChange(row.id, 'reset_date', e.target.value)}
                                className="w-full px-1 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-fearless-blue"
                              />
                            ) : (
                              <span>{formatDateDisplay(row.reset_date)}</span>
                            )}
                          </td>
                        
                          {/* % Complete */}
                          <td style={{ width: columnWidths.percent }} className="px-2 py-2">
                            {isParent ? (
                              <span className="text-sm text-slate-gray bg-gray-100 px-2 py-1 rounded flex items-center justify-center">
                                {displayPercent}<span className="ml-0.5">%</span>
                              </span>
                            ) : user?.role === 'admin' ? (
                              <div className="flex items-center">
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  value={row.percent_complete === 0 || row.percent_complete ? row.percent_complete : ''}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === '') {
                                      handleCellChange(row.id, 'percent_complete', 0);
                                    } else {
                                      handleCellChange(row.id, 'percent_complete', Math.min(100, Math.max(0, parseInt(val) || 0)));
                                    }
                                  }}
                                  onFocus={(e) => {
                                    // Select all text on focus so user can immediately type
                                    e.target.select();
                                  }}
                                  onBlur={(e) => {
                                    // Ensure value is set to 0 if empty on blur
                                    if (e.target.value === '') {
                                      handleCellChange(row.id, 'percent_complete', 0);
                                    }
                                  }}
                                  className="w-12 px-1 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-fearless-blue [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                                <span className="ml-1 text-sm text-slate-gray">%</span>
                              </div>
                            ) : (
                              <span>{row.percent_complete || 0}%</span>
                            )}
                          </td>
                        
                          {/* Notes */}
                          <td style={{ width: columnWidths.notes }} className="px-2 py-2">
                            {user?.role === 'admin' ? (
                              <textarea
                                value={row.notes || ''}
                                onChange={(e) => handleCellChange(row.id, 'notes', e.target.value)}
                                placeholder="Notes..."
                                className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-fearless-blue resize-none whitespace-pre-wrap break-words"
                                style={{ minHeight: '32px', height: 'auto', overflow: 'hidden' }}
                                ref={(el) => {
                                  if (el) {
                                    el.style.height = 'auto';
                                    el.style.height = el.scrollHeight + 'px';
                                  }
                                }}
                                onInput={(e) => {
                                  e.target.style.height = 'auto';
                                  e.target.style.height = e.target.scrollHeight + 'px';
                                }}
                              />
                            ) : (
                              <span className="text-slate-gray whitespace-pre-wrap break-words">{row.notes || '-'}</span>
                            )}
                          </td>
                        
                          {/* Delete */}
                          {user?.role === 'admin' && (
                            <td style={{ width: columnWidths.actions }} className="px-2 py-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteRow(row.id);
                                }}
                                className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                                title="Delete row"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                      });
                    })()
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ProjectDetailPage;
